import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import {
  VideoIcon,
  LanguagesIcon,
  SparklesIcon,
  Upload,
  Mail,
  PlayCircle,
  Download,
  AlertCircle,
  Globe,
  Send,
} from "lucide-react";
import { useVideoProcessing } from "../hooks/useVideoProcessing";
import { useVideoDownload } from "../hooks/useVideoDownload";
import { useTranslations } from "../hooks/useTranslations";
import { SEOHead } from "../components/SEOHead";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { apiService, PaymentNotificationRequest } from "../services/api";
import { useNavigate, useLocation } from "react-router-dom";
import "../components/Header.css";
import {
  getBasePathFromUrlPath,
  getLocalizedPath,
  isDubbingLandingFamilyPath,
  isDubbingLandingPath,
} from "../utils/localizedRoutes";
import {
  getDubbingLandingFaq,
  getDubbingLandingSeo,
  getDubbingPairLandingContent,
} from "../content/dubbingLanding";
import { getDubbingPairByPath } from "../content/dubbingLanguagePairs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function HomePage() {
  const PROCESSING_PRICE_PER_SECOND = 0.099;
  const navigate = useNavigate();
  const location = useLocation();
  const { t, getTranslation, currentLanguage, changeLanguage, availableLanguages } = useTranslations();
  const currentBasePath = getBasePathFromUrlPath(location.pathname);
  const isDubbingLanding = isDubbingLandingPath(location.pathname);
  const isDubbingLandingFamily = isDubbingLandingFamilyPath(location.pathname);
  const dubbingPair = currentBasePath ? getDubbingPairByPath(currentBasePath) : null;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("");
  const [email, setEmail] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeError, setPromoCodeError] = useState("");
  const [promoCodeSuccess, setPromoCodeSuccess] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [basePrice, setBasePrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [emailError, setEmailError] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showEmailSentDialog, setShowEmailSentDialog] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processedVideoUrl, setProcessedVideoUrl] = useState("");
  const [processedFilename, setProcessedFilename] = useState("");
  const [fileValidationError, setFileValidationError] = useState("");
  
  // Standalone subtitles mode is disabled; keep dubbing mode only.
  const [mode] = useState<"dubbing" | "subtitles">("dubbing");
  const [needsTranslation, setNeedsTranslation] = useState(false);
  const [subtitlesLanguage, setSubtitlesLanguage] = useState("");

  // Custom hooks
  const {
    isProcessing,
    progress,
    progressText,
    currentStep,
    error: processingError,
    taskId,
    isUploading,
    uploadProgress,
    startProcessing,
    stopProcessing,
  } = useVideoProcessing();

  const {
    isDownloading,
    downloadVideo,
    downloadDirect,
    error: downloadError,
  } = useVideoDownload();

  // FAQ data - map from translations
  const pairLandingContent = dubbingPair
    ? getDubbingPairLandingContent(currentLanguage, dubbingPair)
    : null;
  const currentFaq = pairLandingContent
    ? pairLandingContent.faq
    : isDubbingLanding
    ? getDubbingLandingFaq(currentLanguage)
    : t.faq.questions;
  const faqData = currentFaq.map((faq, index) => ({
    id: `item-${index + 1}`,
    question: faq.question,
    answer: faq.answer,
  }));

  // Language options (sorted alphabetically by English name)
  const languages = [
    { value: "bg", label: t.languageOptions.bg }, // Bulgarian
    { value: "zh", label: t.languageOptions.zh }, // Chinese
    { value: "hr", label: t.languageOptions.hr }, // Croatian
    { value: "cs", label: t.languageOptions.cs }, // Czech
    { value: "da", label: t.languageOptions.da }, // Danish
    { value: "nl", label: t.languageOptions.nl }, // Dutch
    { value: "en", label: t.languageOptions.en }, // English
    { value: "et", label: t.languageOptions.et }, // Estonian
    { value: "fi", label: t.languageOptions.fi }, // Finnish
    { value: "fr", label: t.languageOptions.fr }, // French
    { value: "de", label: t.languageOptions.de }, // German
    { value: "el", label: t.languageOptions.el }, // Greek
    { value: "hu", label: t.languageOptions.hu }, // Hungarian
    { value: "ga", label: t.languageOptions.ga }, // Irish
    { value: "it", label: t.languageOptions.it }, // Italian
    { value: "ja", label: t.languageOptions.ja }, // Japanese
    { value: "ko", label: t.languageOptions.ko }, // Korean
    { value: "lv", label: t.languageOptions.lv }, // Latvian
    { value: "lt", label: t.languageOptions.lt }, // Lithuanian
    { value: "mt", label: t.languageOptions.mt }, // Maltese
    { value: "pl", label: t.languageOptions.pl }, // Polish
    { value: "pt", label: t.languageOptions.pt }, // Portuguese
    { value: "ro", label: t.languageOptions.ro }, // Romanian
    { value: "ru", label: t.languageOptions.ru }, // Russian
    { value: "sk", label: t.languageOptions.sk }, // Slovak
    { value: "sl", label: t.languageOptions.sl }, // Slovenian
    { value: "es", label: t.languageOptions.es }, // Spanish
    { value: "sv", label: t.languageOptions.sv }, // Swedish
  ];

  // Mock function to get video duration
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  // Promo codes configuration
  const promoCodes: Record<string, number> = {
    ilovevevi: 100, // 100% discount
    missyou: 50,
    hateyou: 50,
    needyou: 50,
    lovehard: 50,
    nowords: 50,
    allyours: 50,
    goaway: 50,
    toomuch: 50,
    coldeyes: 50,
    holdtight: 50,
    forgetme: 50,
    onlyyou: 50,
    whyme: 50,
    sodone: 50,
    dreamon: 50,
    crymore: 50,
    nopebro: 50,
    fakelove: 50,
    overyou: 50,
    tryhard: 50,
    lolsure: 50,
    nextpls: 50,
    sadvibes: 50,
    borednow: 50,
    okboom: 50,
    dramaqueen: 50,
    chillbro: 50,
    bigmad: 50,
    zerofeels: 50,
    byefelicia: 50,
    SmarPoland: 25,
    AroundPRStudio: 25,
    smarglobal: 35,
  };

  // Validate promo code
  const validatePromoCode = (
    code: string
  ): { isValid: boolean; discount: number } => {
    const normalizedCode = code.trim().toLowerCase();
    if (import.meta.env.DEV) {
      console.log("Validating promo code:", {
        original: code,
        normalized: normalizedCode,
      });
    }
    const discount = promoCodes[normalizedCode];
    if (import.meta.env.DEV) {
      console.log("Found discount:", discount);
    }

    if (discount !== undefined) {
      return { isValid: true, discount };
    }

    return { isValid: false, discount: 0 };
  };

  // Calculate price with discount
  const calculatePrice = (
    durationInSeconds: number,
    discountPercent: number = 0
  ): number => {
    const basePrice = Math.round(durationInSeconds * PROCESSING_PRICE_PER_SECOND * 100) / 100;
    const discountAmount = (basePrice * discountPercent) / 100;
    const finalPrice = Math.max(0, basePrice - discountAmount);
    return Math.round(finalPrice * 100) / 100; // Round to 2 decimal places
  };

  // Recalculate final price when discount changes
  const recalculateFinalPrice = (
    basePrice: number,
    discountPercent: number
  ): number => {
    const discountAmount = (basePrice * discountPercent) / 100;
    const finalPrice = Math.max(0, basePrice - discountAmount);
    return Math.round(finalPrice * 100) / 100;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins} ${t.common.minutes} ${secs} ${t.common.seconds}`;
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileSelect = async (file: File) => {
    // Clear previous validation errors
    setFileValidationError("");

    // Check file type
    if (!file.type.startsWith("video/")) {
      setFileValidationError(t.upload.fileTypeError);
      return;
    }

    // Check file size (250 MB = 250 * 1024 * 1024 bytes)
    const maxSizeInBytes = 250 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      const fileSizeInMB = (file.size / 1024 / 1024).toFixed(2);
      setFileValidationError(
        getTranslation("upload.fileSizeError", { size: fileSizeInMB })
      );
      return;
    }

    // Check video duration (15 minutes = 900 seconds)
    try {
      const duration = await getVideoDuration(file);
      const maxDurationInSeconds = 15 * 60; // 15 minutes

      if (duration > maxDurationInSeconds) {
        const durationInMinutes = (duration / 60).toFixed(1);
        setFileValidationError(
          getTranslation("upload.durationError", {
            duration: durationInMinutes,
          })
        );
        return;
      }

      // If all validations pass, set the file
      setSelectedFile(file);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error getting video duration:", error);
      }
      setFileValidationError(t.upload.fileReadError);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setFileValidationError(""); // Clear validation errors
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0] instanceof File) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileValidationError(""); // Clear validation errors
    const file = e.target.files?.[0];
    if (file && file instanceof File) {
      handleFileSelect(file);
    }
  };

  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError(t.payment.enterPromoCode);
      setPromoCodeSuccess("");
      setDiscountPercentage(0);
      return;
    }

    setIsValidatingPromo(true);
    setPromoCodeError("");
    setPromoCodeSuccess("");

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    const validation = validatePromoCode(promoCode);

    if (validation.isValid) {
      setDiscountPercentage(validation.discount);
      setPromoCodeSuccess(
        getTranslation("payment.validPromoCode", {
          discount: validation.discount.toString(),
        })
      );
      setPromoCodeError("");

      // Recalculate final price if we have base price
      if (basePrice > 0) {
        const newFinalPrice = recalculateFinalPrice(
          basePrice,
          validation.discount
        );
        if (import.meta.env.DEV) {
          console.log("Promo code price recalculation:", {
            basePrice,
            discount: validation.discount,
            newFinalPrice,
          });
        }
        setFinalPrice(newFinalPrice);
        (window as any).videoPrice = newFinalPrice;
        (window as any).discountPercentage = validation.discount;
      }
    } else {
      setPromoCodeError(t.payment.invalidPromoCode);
      setPromoCodeSuccess("");
      setDiscountPercentage(0);

      // Reset to base price if we have it
      if (basePrice > 0) {
        setFinalPrice(basePrice);
        (window as any).videoPrice = basePrice;
        (window as any).discountPercentage = 0;
      }
    }

    setIsValidatingPromo(false);
  };

  const handleConvert = async () => {
    if (!selectedFile) return;

    try {
      await apiService.notifyReplaceAudio({ filename: selectedFile.name || "video.mp4" });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("Failed to notify replace-audio click:", error);
      }
    }

    const duration = await getVideoDuration(selectedFile);
    const basePriceValue = calculatePrice(duration, 0);
    const finalPriceValue = recalculateFinalPrice(
      basePriceValue,
      discountPercentage
    );

    if (import.meta.env.DEV) {
      console.log("Price calculation:", {
        duration,
        basePriceValue,
        discountPercentage,
        finalPriceValue,
      });
    }

    // Store data for dialog display
    (window as any).videoDuration = duration;
    (window as any).videoPrice = finalPriceValue;
    (window as any).discountPercentage = discountPercentage;
    (window as any).basePrice = basePriceValue;

    // Update state
    setBasePrice(basePriceValue);
    setFinalPrice(finalPriceValue);

    setShowPaymentDialog(true);
  };

  const handlePayment = async () => {
    // Validate email
    if (!email) {
      setEmailError(t.payment.enterEmail);
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t.payment.enterValidEmail);
      return;
    }

    setEmailError(""); // Clear any previous errors

    if (!selectedFile) {
      alert("Please select a video file");
      return;
    }

    if (mode === "dubbing") {
      if (!targetLanguage || !sourceLanguage) {
        alert("Please select both source and target languages");
        return;
      }
    }
    
    if (mode === "subtitles" && needsTranslation) {
      if (!targetLanguage) {
        alert("Please select translation language");
        return;
      }
    }

    setShowPaymentDialog(false);
    setShowProgressDialog(true);

    try {
      // Get video duration
      const duration = await getVideoDuration(selectedFile);

      // Use the current final price
      const cost = finalPrice || (window as any).videoPrice || 0;

      // Determine source and target languages based on mode (same logic as old project)
      const finalSourceLanguage = mode === "subtitles" ? (sourceLanguage || "") : sourceLanguage;
      const finalTargetLanguage = mode === "subtitles" && needsTranslation 
        ? targetLanguage 
        : (mode === "subtitles" ? "" : targetLanguage);

      if (import.meta.env.DEV) {
        console.log("Starting video processing with:", {
          video: selectedFile.name,
          email: email,
          duration: duration,
          cost: cost,
          mode: mode,
          source_language: finalSourceLanguage,
          target_language: finalTargetLanguage,
          add_subtitles: false,
          speed: 0.85,
        });
      }

      await startProcessing({
        video: selectedFile,
        email: email,
        duration: duration,
        cost: cost,
        source_language: finalSourceLanguage,
        target_language: finalTargetLanguage,
        speed: 0.85, // Use default recommended speed
        add_subtitles: false,
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Failed to start processing:", error);
      }
      setShowProgressDialog(false);
      alert("Failed to start video processing. Please try again.");
    }
  };

  // Handle processing completion
  useEffect(() => {
    if (!isProcessing && taskId && currentStep === "complete") {
      setShowProgressDialog(false);
      setShowResultDialog(true);
      setProcessedFilename(`video_dubbed_${taskId}.mp4`);

      // Use real processed video URL if available
      const realVideoUrl = (window as any).processedVideoUrl;
      if (realVideoUrl) {
        setProcessedVideoUrl(realVideoUrl);
      } else {
        // Fallback: construct download URL using the same API base URL
        const downloadUrl = apiService.getDownloadUrl(taskId);
        setProcessedVideoUrl(downloadUrl);
      }
    }
  }, [isProcessing, taskId, currentStep]);

  // Handle processing errors
  useEffect(() => {
    if (processingError) {
      setShowProgressDialog(false);
      alert(`Processing error: ${processingError}`);
    }
  }, [processingError]);

  const canConvert = 
    mode === "subtitles" && !needsTranslation
      ? selectedFile && !fileValidationError
      : mode === "subtitles" && needsTranslation
      ? selectedFile && targetLanguage && !fileValidationError
      : selectedFile && sourceLanguage && targetLanguage && !fileValidationError;

  // Generate dynamic subtitle with correct price based on mode
  const getPromoSubtitle = () => {
    const baseSubtitle = t.header.subtitle;
    const price = "0.099";
    // Replace price in subtitle (works with different formats / older values)
    return baseSubtitle.replace(/0\.0314|0\.0100|0\.01|\$0\.0314|\$0\.0100|\$0\.01/g, `$${price}`);
  };

  const dubbingLandingSeo = pairLandingContent || getDubbingLandingSeo(currentLanguage);
  const heroTitle = isDubbingLandingFamily
    ? dubbingLandingSeo.heroTitle
    : t.header.title;
  const heroDescription = isDubbingLandingFamily
    ? dubbingLandingSeo.description
    : getPromoSubtitle();
  const promoSubtitle = getPromoSubtitle();

  return (
    <>
      <SEOHead
        pageKey={isDubbingLandingFamily ? "dubbingLanding" : "home"}
        overrideSeo={isDubbingLandingFamily ? dubbingLandingSeo : undefined}
        canonicalPathOverride={isDubbingLandingFamily && currentBasePath ? currentBasePath : undefined}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-800 pt-32">
        <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12 mt-12">
          <h1 className="text-white mb-4">{heroTitle}</h1>
          <p className="text-purple-100">{heroDescription}</p>
          {isDubbingLandingFamily && (
            <p className="text-purple-100 mt-2">{promoSubtitle}</p>
          )}
        </div>

        {/* Main card */}
        <Card className="max-w-2xl mx-auto bg-white/95 backdrop-blur-sm shadow-xl">
          <CardContent className="p-8">
            {/* Step 1: Video Upload */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center" aria-hidden="true">
                  1
                </div>
                <h2 className="flex items-center gap-2 text-xl font-semibold">
                  <VideoIcon className="w-5 h-5" aria-hidden="true" />
                  {t.steps.uploadVideo}
                </h2>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-4 md:p-8 text-center transition-colors max-w-full overflow-hidden ${isDragOver
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-300 bg-gray-50"
                  }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {selectedFile ? (
                  <div className="text-green-600 max-w-full">
                    <VideoIcon className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2" />
                    <p
                      className="text-xs md:text-sm truncate w-full whitespace-nowrap overflow-hidden"
                      title={selectedFile.name}
                    >
                      {t.upload.selectedFile} {selectedFile.name}
                    </p>
                    <p className="text-xs md:text-sm text-gray-700">
                      {t.upload.size}{" "}
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : fileValidationError ? (
                  <div className="text-red-600 max-w-full">
                    <AlertCircle className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2" />
                    <p className="text-xs md:text-sm text-center">
                      {fileValidationError}
                    </p>
                  </div>
                ) : (
                  <div className="text-gray-700">
                    <Upload className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2 md:mb-4" />
                    <p className="mb-1 md:mb-2 text-sm md:text-base font-medium">
                      {t.upload.dragAndDrop}
                    </p>
                    <p className="mb-2 md:mb-4 text-sm md:text-base">
                      {t.upload.orClickToSelect}
                    </p>

                    <div className="text-xs md:text-sm text-gray-600 space-y-1">
                      <p>{t.upload.supportedFormats}</p>
                      <p>{t.upload.maxFileSize}</p>
                      <p>{t.upload.maxDuration}</p>
                      <p>{t.upload.processingTime}</p>
                    </div>
                  </div>
                )}

                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileInput}
                  className="hidden"
                  id="video-upload"
                  aria-label="Upload video file"
                />
                <label
                  htmlFor="video-upload"
                  className="inline-block"
                >
                  <Button
                    variant="outline"
                    className="mt-2 md:mt-4 cursor-pointer text-sm md:text-base"
                    asChild
                  >
                    <span role="button" tabIndex={0} aria-label="Choose video file to upload">
                      Choose File
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Step 2: Language Selection */}
            {(mode === "dubbing" || mode === "subtitles") && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center" aria-hidden="true">
                    2
                  </div>
                  <h2 className="flex items-center gap-2 text-xl font-semibold">
                    <LanguagesIcon className="w-5 h-5" aria-hidden="true" />
                    Select Languages
                  </h2>
                </div>

                {/* Dubbing mode */}
                {mode === "dubbing" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-2">
                          {t.languages.sourceAudioLanguage}
                        </label>
                        <Select
                          value={sourceLanguage}
                          onValueChange={setSourceLanguage}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t.languages.selectLanguage} />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-2">
                          {t.languages.targetAudioLanguage}
                        </label>
                        <Select
                          value={targetLanguage}
                          onValueChange={setTargetLanguage}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t.languages.selectLanguage} />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}

                {/* Subtitles mode */}
                {mode === "subtitles" && (
                  <>
                    {/* Checkbox: нужен ли перевод */}
                    <div className="mb-4 flex items-center space-x-2">
                      <Checkbox
                        id="needs-translation"
                        checked={needsTranslation}
                        onCheckedChange={(checked) => {
                          setNeedsTranslation(checked === true);
                          if (!checked) {
                            setTargetLanguage("");
                          }
                        }}
                      />
                      <label
                        htmlFor="needs-translation"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {t.services?.needsTranslation || "Нужен перевод?"}
                      </label>
                    </div>

                    {/* Если чекбокс активен - показываем выбор языка перевода */}
                    {needsTranslation && (
                      <div className="mb-4">
                        <label className="block text-sm text-gray-600 mb-2">
                          {t.services?.translationLanguage || "Язык перевода"}
                        </label>
                        <Select
                          value={targetLanguage}
                          onValueChange={setTargetLanguage}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t.languages.selectLanguage} />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Conversion */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center" aria-hidden="true">
                  3
                </div>
                <h2 className="flex items-center gap-2 text-xl font-semibold">
                  <SparklesIcon className="w-5 h-5" aria-hidden="true" />
                  {t.steps.conversionResult}
                </h2>
              </div>

              <div className="text-center">
                <Button
                  onClick={handleConvert}
                  disabled={!canConvert}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8 py-3"
                  aria-label={mode === "subtitles" ? "Generate Subtitles" : t.conversion.replaceAudio}
                  aria-disabled={!canConvert}
                >
                  {mode === "subtitles" ? (t.conversion?.createSubtitles || "Создать субтитры") : t.conversion.replaceAudio}
                </Button>

                {!canConvert && (
                  <p className="text-sm text-gray-600 mt-2">
                    {fileValidationError
                      ? t.conversion.fixValidationError
                      : mode === "subtitles" && !needsTranslation
                      ? (t.conversion?.uploadVideoForSubtitles || "Загрузите видео для создания субтитров")
                      : mode === "subtitles" && needsTranslation && !targetLanguage
                      ? (t.conversion?.selectTranslationLanguage || "Выберите язык перевода")
                      : t.conversion.uploadVideoAndSelectLanguages}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Demo Video Section */}
        <section className="mt-8 max-w-2xl mx-auto" aria-label="Demo Video">
          <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-4">
                <h2 className="text-gray-700 mb-2">{t.demo.seeVeviInAction}</h2>
                <p className="text-sm text-gray-600">
                  {t.demo.watchHowItWorks}
                </p>
              </div>

              <div className="bg-black rounded-lg overflow-hidden">
                <video
                  controls
                  controlsList="nodownload"
                  className="w-full h-64 object-contain"
                  aria-label="VEVI demo video showing voice translation in action"
                >
                  <source src="/VEVI_promo.mp4" type="video/mp4" />
                  Your browser does not support video
                </video>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-sm">
                <div className="bg-blue-50 p-3 rounded-lg min-w-0">
                  <h3 className="text-blue-800 mb-1 break-words font-semibold">
                    {t.demo.beforeEnglish}
                  </h3>
                  <p className="text-blue-700 text-xs break-words">
                    {t.demo.originalPresentation}
                  </p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg min-w-0">
                  <h3 className="text-green-800 mb-1 break-words font-semibold">
                    {t.demo.afterSpanish}
                  </h3>
                  <p className="text-green-700 text-xs break-words">
                    {t.demo.aiTranslatedVoice}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t.payment.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="mb-3 font-semibold text-lg">{t.payment.processingCost}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t.payment.duration}</span>
                    <span>
                      {(window as any).videoDuration
                        ? formatDuration((window as any).videoDuration)
                        : "4 minutes 45 seconds"}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    <div>
                      Base cost ={" "}
                      {(window as any).videoDuration
                        ? `${Math.floor(
                          (window as any).videoDuration / 60
                        )} min ${Math.floor(
                          (window as any).videoDuration % 60
                        )} sec`
                        : "4 min 45 sec"}{" "}
                      × ${PROCESSING_PRICE_PER_SECOND.toFixed(3)}/sec = $
                      {basePrice || (window as any).basePrice || 0}
                    </div>
                  </div>

                  {discountPercentage > 0 && (
                    <>
                      <div className="flex justify-between text-green-600">
                        <span>
                          {getTranslation("payment.discount", { promoCode })}:
                        </span>
                        <span>-{discountPercentage}%</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{t.payment.discountAmount}:</span>
                        <span>
                          -$
                          {(
                            ((basePrice || (window as any).basePrice || 0) *
                              discountPercentage) /
                            100
                          ).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="border-t pt-2 flex justify-between items-center">
                    <span>{t.payment.total}</span>
                    <span className="text-xl text-purple-600">
                      ${finalPrice || (window as any).videoPrice || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  {t.payment.emailToReceiveResult}
                </label>
                <Input
                  type="email"
                  placeholder={t.payment.emailPlaceholder}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(""); // Clear error when user types
                  }}
                  className={`w-full ${emailError ? "border-red-500" : ""}`}
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  {t.payment.promoCode}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t.payment.promoCodePlaceholder}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      setPromoCodeError("");
                      setPromoCodeSuccess("");
                      setDiscountPercentage(0);

                      // Reset to base price if we have it
                      if (basePrice > 0) {
                        setFinalPrice(basePrice);
                        (window as any).videoPrice = basePrice;
                        (window as any).discountPercentage = 0;
                      }
                    }}
                    className={`flex-1 ${promoCodeError
                        ? "border-red-500"
                        : promoCodeSuccess
                          ? "border-green-500"
                          : ""
                      }`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePromoCodeValidation}
                    disabled={isValidatingPromo || !promoCode.trim()}
                    className="px-4"
                    aria-label="Validate promo code"
                  >
                    {isValidatingPromo ? t.payment.checking : t.payment.check}
                  </Button>
                </div>
                {promoCodeError && (
                  <p className="text-red-500 text-sm mt-1">{promoCodeError}</p>
                )}
                {promoCodeSuccess && (
                  <p className="text-green-600 text-sm mt-1">
                    {promoCodeSuccess}
                  </p>
                )}
              </div>

              {/* <div className="flex items-start space-x-3 py-3 px-4 bg-blue-50 rounded-lg border border-blue-200">
                <Checkbox
                  id="add-subtitles"
                  checked={addSubtitles}
                  onCheckedChange={(checked) => setAddSubtitles(checked === true)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label
                    htmlFor="add-subtitles"
                    className="text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    {t.payment.addSubtitles}
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    {t.payment.addSubtitlesDescription}
                  </p>
                </div>
              </div> */}

              <div className="text-sm text-gray-600">
                <p>{t.payment.highQualityTranslation}</p>
                <p>{t.payment.originalVideoPreserved}</p>
                <p>{t.payment.fastProcessing}</p>
                <p>{t.payment.resultWillBeSent}</p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentDialog(false)}
                  className="flex-1"
                  aria-label="Cancel payment"
                >
                  {t.payment.cancel}
                </Button>
                <Button
                  onClick={handlePayment}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  aria-label="Continue to payment"
                >
                  {t.payment.continue}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Progress Dialog */}
        <Dialog open={showProgressDialog} onOpenChange={() => { }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t.progress.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SparklesIcon className="w-8 h-8 text-purple-600" />
                </div>
                <p className="text-lg">{progressText}</p>
                {currentStep && (
                  <p className="text-sm text-blue-600">
                    {t.progress.step} {currentStep}
                  </p>
                )}
              </div>

              {/* Показываем прогресс-бар только при загрузке файла */}
              {isUploading ? (
                <div className="space-y-2">
                  <Progress value={uploadProgress} className="w-full" />
                  <p className="text-sm text-gray-700 text-center font-medium">
                    {uploadProgress}%
                  </p>
                </div>
              ) : (
                // После загрузки - просто крутящийся лоадер без процентов
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              )}

              {processingError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {t.progress.error}
                    </span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">{processingError}</p>
                </div>
              )}

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p>{t.progress.uploadingAndExtracting}</p>
                <p>{t.progress.transcribingAndTranslating}</p>
                <p>{t.progress.synthesizingAudio}</p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    stopProcessing();
                    setShowProgressDialog(false);
                  }}
                  className="flex-1"
                  aria-label="Cancel video processing"
                >
                  {t.payment.cancel}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Result Dialog */}
        <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t.result.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PlayCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-green-600">{t.result.conversionCompleted}</p>
              </div>

              {/* Video Player */}
              <div className="space-y-2">
                <p className="text-sm text-gray-600 text-center">
                  {t.result.processedVideoDescription}
                </p>
                <div className="bg-black rounded-lg overflow-hidden">
                  <video
                    src={processedVideoUrl}
                    controls
                    controlsList="nodownload"
                    preload="metadata"
                    crossOrigin="anonymous"
                    playsInline
                    webkit-playsinline="true"
                    className="w-full h-64 object-contain"
                    poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMDAwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiNmZmYiIGR5PSIuM2VtIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Qcm9jZXNzZWQgVmlkZW88L3RleHQ+PC9zdmc+"
                    aria-label="Your processed video with voice translation"
                    onError={(e) => {
                      if (import.meta.env.DEV) {
                        console.error("Video load error:", e);
                        console.error("Video URL:", processedVideoUrl);
                      }
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.style &&
                        (e.currentTarget.nextElementSibling.style.display = "block");
                    }}
                    onLoadStart={() => {
                      if (import.meta.env.DEV) {
                        console.log("Video loading started:", processedVideoUrl);
                      }
                    }}
                    onCanPlay={() => {
                      if (import.meta.env.DEV) {
                        console.log("Video can play:", processedVideoUrl);
                      }
                    }}
                  >
                    Your browser does not support video
                  </video>
                  <div className="hidden w-full h-64 flex items-center justify-center text-white bg-gray-800">
                    <div className="text-center">
                      <p className="mb-2">{t.result.videoNotAvailable}</p>
                      <p className="text-sm text-gray-200">
                        {t.result.useDownloadButton}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => {
                  setShowEmailSentDialog(true);
                }}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                aria-label="Pay and download processed video"
              >
                <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                {t.result.payAndDownload}
              </Button>
            </div>

            {downloadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t.result.downloadError}
                  </span>
                </div>
                <p className="text-sm text-red-600 mt-1">{downloadError}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Sent Dialog */}
        <Dialog
          open={showEmailSentDialog}
          onOpenChange={setShowEmailSentDialog}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t.emailSent.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-lg text-green-600 mb-2">
                  {t.emailSent.videoOnWay}
                </p>
                <p className="text-sm text-gray-600">
                  {t.emailSent.checkInbox}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="mb-2 text-sm font-semibold">
                  {t.emailSent.whatsNext}
                </h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>{getTranslation("emailSent.checkEmail", { email })}</p>
                  <p>{t.emailSent.checkSpam}</p>
                  <p>{t.emailSent.downloadAndEnjoy}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailSentDialog(false)}
                  className="flex-1"
                  aria-label="Close dialog"
                >
                  {t.emailSent.close}
                </Button>
                <Button
                  onClick={() => {
                    setShowEmailSentDialog(false);
                    setShowResultDialog(false);
                    // Reset form
                    setSelectedFile(null);
                    setSourceLanguage("");
                    setTargetLanguage("");
                    setEmail("");
                    setPromoCode("");
                    setPromoCodeError("");
                    setPromoCodeSuccess("");
                    setDiscountPercentage(0);
                    setBasePrice(0);
                    setFinalPrice(0);
                    setFileValidationError("");
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  aria-label="Process another video"
                >
                  {t.emailSent.processAnotherVideo}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* FAQ Section */}
        <section className="mt-16" aria-label="FAQ">
          <div className="text-center mb-8">
            <h2 className="text-white text-3xl mb-2">{t.faq.title}</h2>
            <p className="text-purple-100">{t.faq.subtitle}</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-6">
                <Accordion type="single" collapsible className="w-full">
                  {faqData.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-left hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>

        </div>

        {/* Footer */}
        <Footer onShowBlog={() => {
          navigate(getLocalizedPath('/blog', currentLanguage));
        }} />
      </main>
    </>
  );
}
